import { Guid } from "client_legacy/ndaw/guid";
import * as rssService from "client/Api/Workspace/LiveInformation/Rss/rssLiveInformation";
import { HttpRequestError } from "client/Api/httpClient";
import { InterestedParty, LiveInformationProvider, SuccessfulCacheItem } from "client_legacy/Workspace/LiveInformation/liveInformationProvider";

interface SuccessRssFeedModel {
	tabUniqueId: Guid;
	status: "success";
	items: rssService.RssItemModel[];
}
interface ErrorRssFeedModel {
	tabUniqueId: Guid;
	status: "error";
}

export type RssFeedModel = SuccessRssFeedModel | ErrorRssFeedModel;

interface RssLiveInformationProviderOptions {
	intervalInSeconds: number;
}

export type RssResourceId = {
	tileId: number;
	tabId: Guid;
};

export type RssInterests = {
	items?: boolean;
};

export interface RssInterestedParty extends InterestedParty<Guid, RssResourceId> {
	updateFeed(feedModel: RssFeedModel): void;
}

export class RssLiveInformationProvider extends LiveInformationProvider<
	RssLiveInformationProviderOptions,
	Guid,
	RssResourceId,
	RssInterests,
	RssInterestedParty,
	RssFeedModel
>
{
	protected async getDataFromBackend(
		entries: readonly [RssResourceId, RssInterests][]
	): Promise<readonly [RssResourceId, RssFeedModel][]> {
		const result: [RssResourceId, RssFeedModel][] = [];

		for (const entry of entries) {
			const [resourceId, interests] = entry;
			let feed: RssFeedModel | undefined;
			if (interests.items) {
				feed = await this.getFeed(
					resourceId.tileId,
					resourceId.tabId
				);
			} else {
				throw new Error("Unknown interests");
			}

			result.push([resourceId, feed]);
		}

		return result;
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<RssInterests, RssFeedModel>,
		response: [RssInterests, RssFeedModel]
	): void {
		const [interests, model] = response;
		if (interests.items) {
			cacheItem.retrievedInterests.items = true;
			cacheItem.data = model;
		}
	}

	protected informParty(
		party: RssInterestedParty,
		cacheItem: [RssInterests, RssFeedModel]
	): void {
		const [retrievedInterests, response] = cacheItem;
		const interestedIn: RssInterests = this.getInterestsForEnsuredParty(party);

		if (interestedIn.items && retrievedInterests.items) {
			party.updateFeed(response);
		}
	}

	protected override createResourceMap<TValue>(): Map<RssResourceId, TValue> {
		return new RssFeedMap();
	}

	private async getFeed(tileId: number, tabId: Guid): Promise<RssFeedModel> {
		try {
			const feedItems: rssService.RssItemModel[] = await rssService.getFeed(tileId, tabId);
			return {
				tabUniqueId: tabId,
				status: "success",
				items: feedItems
			};
		} catch(e) {
			if (e instanceof (HttpRequestError) && e.statusCode === 403) {
				throw e;
			}

			return {
				tabUniqueId: tabId,
				status: "error"
			};
		}
	}
}

class RssFeedMap<TValue> extends Map<RssResourceId, TValue>
{
	protected normalizeKey(key: RssResourceId): string {
		return `${key.tileId}_${key.tabId}`;
	}
}